// File: src/pages/PendingPurchase.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  Table,
  Label,
} from "reactstrap";

import { ImDownload } from "react-icons/im";
import { Eye, Edit, Trash2 } from "react-feather";
import { IoMdRemoveCircleOutline } from "react-icons/io";
import {
  FaArrowAltCircleLeft,
  FaArrowAltCircleRight,
  FaFilter,
} from "react-icons/fa";
import {
  BsFillArrowDownSquareFill,
  BsFillArrowUpSquareFill,
} from "react-icons/bs";
import swal from "sweetalert";

// ---- shared imports (per your instruction) ----
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";
import {
  PurchaseOrderList,
  _Put,
  _Post,
  _Delete,
} from "../import/ApiEndPoint/ApiCalling";
import {
  Completed_Order_Delete,
  Place_Order_Delete,
  Purchase_Invoice_Create,
  Purchase_Status_Order,
} from "../import/ApiEndPoint/Api";
import { convertDataCSVtoExcel, exportDataToPDF } from "../import/Downloader";

// ---- tiny page-scoped CSS injected once ----
const injectOnce = () => {
  if (document.getElementById("pending-po-inline-css")) return;
  const style = document.createElement("style");
  style.id = "pending-po-inline-css";
  style.innerHTML = `
    .po-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:900; padding:6px 10px; border-radius:999px;
    }
    .po-card{ border:1px solid #e5e7eb; border-radius:14px; background:#f4f7f6; padding:10px 12px; }
    .po-title{ font-weight:900; text-transform:uppercase; font-size:18px; letter-spacing:.02em; display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
    .po-actions{ display:flex; gap:8px; align-items:center; flex-wrap:wrap; justify-content:flex-end; }
    .po-search{ min-width:260px; }
    .po-btn{
      height:34px; display:flex; align-items:center; justify-content:center; gap:8px;
      border-radius:12px; font-weight:800; letter-spacing:.02em; text-transform:uppercase;
    }
    .po-btn-outline{
      height:34px; display:flex; align-items:center; justify-content:center; gap:8px;
      border-radius:12px; font-weight:800; letter-spacing:.02em;
      background:#fff;
    }

    .po-filterbar{
      margin-top:10px;
      background:#fff;
      border:1px dashed #cbd5e1;
      border-radius:14px;
      padding:10px 12px;
    }
    .po-filter-row{
      display:flex;
      gap:10px;
      flex-wrap:wrap;
      align-items:flex-end;
    }
    .po-fitem{ flex:1 1 180px; min-width:180px; }
    .po-fitem label{ font-size:12px; font-weight:900; color:#0f172a; letter-spacing:.02em; margin-bottom:4px; }
    .po-fitem .form-control{ border-radius:12px; }
    .po-chipline{ margin-top:10px; display:flex; flex-wrap:wrap; gap:8px; }
    .po-chip{
      display:inline-flex; align-items:center; gap:8px;
      padding:6px 10px; border-radius:999px;
      background:#f1f5f9; border:1px solid #e5e7eb; color:#0f172a;
      font-weight:900; font-size:12px; letter-spacing:.02em;
    }

    .po-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    table.po-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:1000px; }
    .po-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:900; text-transform:uppercase; font-size:12px; letter-spacing:.04em;
      border-bottom:1px solid #e5e7eb; padding:8px 10px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .po-table tbody td{
      padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .po-table tbody tr:hover{ background:#f9fbff; }

    .action-chips{ display:flex; justify-content:center; align-items:center; gap:10px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer; }
    .chip-view{ background:#39cccc; }
    .chip-edit{ background:#2563eb; }
    .chip-del{ background:#ef4444; }

    .picker-list{ border:1px solid #e5e7eb; border-radius:10px; max-height:280px; overflow:auto; background:#fff; padding:8px; }
    .pick-item{ padding:8px 10px; border-radius:8px; border:1px solid #e5e7eb; margin-bottom:6px; background:#fff; display:flex; align-items:center; gap:8px; cursor:pointer; }

    .paging{ display:flex; gap:8px; align-items:center; justify-content:flex-end; padding:8px 0 }
    .nowrap{ white-space:nowrap; }
  `;
  document.head.appendChild(style);
};

// helpers
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const includesCompleted = (s = "") =>
  String(s).toLowerCase().includes("completed");

const toNum = (v, d = 0) => {
  const n = Number(String(v ?? "").replace(/[^\d.\-]/g, ""));
  return Number.isFinite(n) ? n : d;
};

const getRowDate = (r) => {
  const d = r?.createdAt || r?.date || r?.updatedAt || r?.created_at;
  const t = d ? new Date(d) : null;
  return t && !isNaN(+t) ? t : null;
};

// export CSV builder (safe)
const buildCSV = (data, cols) => {
  const head = cols.map((c) => c.header).join(",");
  const lines = (data || []).map((r, ridx) =>
    cols
      .map((c) => {
        const raw = typeof c.get === "function" ? c.get(r, ridx) : r?.[c.key];
        const value = toStr(raw).replace(/"/g, '""');
        return `"${value}"`;
      })
      .join(","),
  );
  return [head, ...lines].join("\r\n");
};

export default function PendingPurchase() {
  const navigate = useNavigate();
  const hideTimer = useRef(null);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [rows, setRows] = useState([]); // filtered server list (Received + Active)
  const [allRows, setAllRows] = useState([]); // kept for parity
  const [search, setSearch] = useState("");

  const [masterShow, setMasterShow] = useState(false);

  // redesigned filters
  const [showFilters, setShowFilters] = useState(false);
  const [fStatus, setFStatus] = useState("All"); // because your list uses Received; status can vary
  const [fCompany, setFCompany] = useState("");
  const [fFrom, setFFrom] = useState(""); // yyyy-mm-dd
  const [fTo, setFTo] = useState(""); // yyyy-mm-dd
  const [fMinGrand, setFMinGrand] = useState("");
  const [fMaxGrand, setFMaxGrand] = useState("");

  // column chooser modal
  const [modal, setModal] = useState(false);
  const [arrIndex, setArrIndex] = useState(-1);
  const [checkedToAdd, setCheckedToAdd] = useState([]);

  // selected visible columns
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [pageSize, setPageSize] = useState(15);
  const [page, setPage] = useState(0);

  // view modal
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  useEffect(() => injectOnce(), []);

  // Define base columns
  const baseColumns = useMemo(
    () => [
      { key: "_uid", header: "UID", get: (_r, i) => i + 1 },
      {
        key: "_actions",
        header: "ACTIONS",
        get: () => "",
        render: (r) => (
          <div className="action-chips">
            {permissions?.View && (
              <span
                className="chip chip-view"
                title="View"
                onClick={() => openView(r)}
              >
                <Eye size={14} color="#fff" />
              </span>
            )}
            {permissions?.Edit && (
              <span
                className="chip chip-edit"
                title="Edit"
                onClick={() =>
                  navigate(`/app/AJgroup/order/editPurchase/${r?._id}`, {
                    state: r,
                  })
                }
              >
                <Edit size={14} color="#fff" />
              </span>
            )}
            {permissions?.Delete && (
              <span
                className="chip chip-del"
                title="Delete"
                onClick={() => handleDelete(r?._id, r?.status)}
              >
                <Trash2 size={14} color="#fff" />
              </span>
            )}
          </div>
        ),
      },
      { key: "status", header: "STATUS", get: (r) => r?.status || "-" },
      {
        key: "partyId.contactNumber",
        header: "CONTACT",
        get: (r) => r?.partyId?.contactNumber || "-",
      },
      {
        key: "partyId.CompanyName",
        header: "COMPANY",
        get: (r) => r?.partyId?.CompanyName || "-",
      },
      {
        key: "partyId.ownerName",
        header: "OWNER",
        get: (r) => r?.partyId?.ownerName || "-",
      },
      {
        key: "invoiceId",
        header: "INVOICE NO.",
        get: (r) => r?.invoiceId || "-",
      },
      {
        key: "BuiltyNumber",
        header: "BUILTY NO.",
        get: (r) => r?.BuiltyNumber || "-",
      },
      {
        key: "NoOfPackage",
        header: "PACKAGES",
        get: (r) => r?.NoOfPackage || "-",
      },
      {
        key: "vehicleNo",
        header: "VEHICLE NO",
        get: (r) => r?.vehicleNo || "-",
      },
      {
        key: "Tax",
        header: "TAX",
        get: (r) =>
          toNum(r?.igstTotal, 0) > 0
            ? toNum(r?.igstTotal, 0)
            : toNum(r?.cgstTotal, 0) + toNum(r?.sgstTotal, 0),
      },
      {
        key: "coolieAndCartage",
        header: "CHARGES",
        get: (r) => toNum(r?.coolieAndCartage, 0),
      },
      { key: "amount", header: "AMOUNT", get: (r) => toNum(r?.amount, 0) },
      {
        key: "roundOff",
        header: "ROUND OFF",
        get: (r) => toNum(r?.roundOff, 0),
      },
      {
        key: "grandTotal",
        header: "GRAND TOTAL",
        get: (r) => toNum(r?.grandTotal, 0),
      },
    ],
    [navigate, permissions],
  );

  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    return m;
  }, [baseColumns]);

  // load persisted visible columns or default
  useEffect(() => {
    const raw = localStorage.getItem("PurchasePending");
    let next = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        const maybeKeys = parsed.map((c) => c.key || c?.field || c);
        next = maybeKeys.filter((k) => colMap.has(k));
      }
    } catch {}
    if (!next.length) next = baseColumns.map((c) => c.key);
    setSelectedKeys(next);
  }, [baseColumns, colMap]);

  // fetch data
  const reload = async (uid, db) => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await PurchaseOrderList(uid, db);
      const list = Array.isArray(res?.orderHistory) ? res.orderHistory : [];

      // your original logic
      const received = list.filter((x) => x?.status === "Received");
      const active = received.filter((x) => x?.status !== "Deactive");

      const enriched = active.map((x) => ({
        ...x,
        Tax:
          toNum(x?.igstTotal, 0) > 0
            ? toNum(x?.igstTotal, 0)
            : toNum(x?.cgstTotal, 0) + toNum(x?.sgstTotal, 0),
      }));

      const final = enriched.slice().reverse();
      setRows(final);
      setAllRows(final);
    } catch (e) {
      console.error("[PendingPurchase] fetch error:", e);
      setErrorMsg(
        e?.response?.data?.message ||
          e?.message ||
          "Unable to load pending purchases.",
      );
      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const inside = CheckPermission("Purchase Pending");
      if (inside && typeof inside === "object") {
        setPermissions((p) => ({ ...p, ...inside }));
      }
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const financialYearSelected = localStorage.getItem(
        "financialYearSelected",
      );
      const selectedDatabase = `${user?.database}-${financialYearSelected}`;
      if (user?.rolename?.roleName === "MASTER") setMasterShow(true);
      await reload(user?._id, selectedDatabase);
    })();
  }, []);

  // SuperAdmin change
  const onMasterSubmit = (e) => {
    e.preventDefault();
    const pack = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || '""',
    );
    if (!pack) return;
    const [id, db] = String(pack).split(" ");
    reload(id, db);
  };
  const onMasterChange = (v) =>
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(v));

  // row actions
  const openView = (r) => {
    setViewData(r);
    setViewOpen(true);
  };

  const handleDelete = (id, status) => {
    const URL = includesCompleted(status)
      ? Completed_Order_Delete
      : Place_Order_Delete;
    swal("Warning", "Sure You Want to Delete it", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then(async (value) => {
      if (value === "delete") {
        try {
          await _Delete(URL, id);
          setRows((prev) => prev.filter((x) => x?._id !== id));
          setAllRows((prev) => prev.filter((x) => x?._id !== id));
        } catch (e) {
          // silent
        }
      }
    });
  };

  // (kept for parity; not surfaced in UI right now)
  const updateStatus = async (row, status) => {
    const payload = { status };
    const clone = { ...row, status };
    try {
      await _Put(Purchase_Status_Order, row?._id, payload);
      await _Post(Purchase_Invoice_Create, row?._id, clone);
      const u = JSON.parse(localStorage.getItem("userData") || "{}");
      const financialYearSelected = localStorage.getItem(
        "financialYearSelected",
      );
      const selectedDatabase = `${u?.database}-${financialYearSelected}`;
      await reload(u?._id, selectedDatabase);
    } catch (e) {
      // ignore
    }
  };

  // column picker modal logic
  const openPicker = () => {
    setCheckedToAdd([]);
    setArrIndex(-1);
    setModal(true);
  };
  const closePicker = () => setModal(false);

  const handleCheckToAdd = (checked, key) => {
    setCheckedToAdd((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return Array.from(next);
    });
  };

  const addSelectedColumns = () => {
    setSelectedKeys((prev) => Array.from(new Set([...prev, ...checkedToAdd])));
  };

  const removeAtIndex = () => {
    if (arrIndex < 0) return;
    setSelectedKeys((prev) => {
      const next = prev.slice();
      next.splice(arrIndex, 1);
      return next;
    });
    setArrIndex(-1);
  };

  const shiftUp = () => {
    if (arrIndex <= 0) return;
    setSelectedKeys((prev) => {
      const next = prev.slice();
      const el = next.splice(arrIndex, 1)[0];
      next.splice(arrIndex - 1, 0, el);
      return next;
    });
    setArrIndex((i) => i - 1);
  };

  const shiftDown = () => {
    setSelectedKeys((prev) => {
      if (arrIndex < 0 || arrIndex >= prev.length - 1) return prev;
      const next = prev.slice();
      const el = next.splice(arrIndex, 1)[0];
      next.splice(arrIndex + 1, 0, el);
      setArrIndex((i) => i + 1);
      return next;
    });
  };

  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("PurchasePending", JSON.stringify(selectedKeys));
    closePicker();
  };

  // ---------------- FILTERS (new redesigned) ----------------
  const filteredByFilters = useMemo(() => {
    const st = String(fStatus || "")
      .trim()
      .toLowerCase();
    const companyTerm = String(fCompany || "")
      .trim()
      .toLowerCase();

    const from = fFrom ? new Date(`${fFrom}T00:00:00`) : null;
    const to = fTo ? new Date(`${fTo}T23:59:59`) : null;

    const minG = fMinGrand !== "" ? Number(fMinGrand) : null;
    const maxG = fMaxGrand !== "" ? Number(fMaxGrand) : null;

    return (rows || []).filter((r) => {
      if (st && st !== "all") {
        if (
          String(r?.status || "")
            .trim()
            .toLowerCase() !== st
        )
          return false;
      }

      if (companyTerm) {
        const nm = String(r?.partyId?.CompanyName || "").toLowerCase();
        if (!nm.includes(companyTerm)) return false;
      }

      const dt = getRowDate(r);
      if (from && dt && dt < from) return false;
      if (to && dt && dt > to) return false;

      const g = toNum(r?.grandTotal, 0);
      if (minG !== null && Number.isFinite(minG)) {
        if (g < minG) return false;
      }
      if (maxG !== null && Number.isFinite(maxG)) {
        if (g > maxG) return false;
      }

      return true;
    });
  }, [rows, fStatus, fCompany, fFrom, fTo, fMinGrand, fMaxGrand]);

  // search & visible columns
  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap],
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return filteredByFilters;

    const searchableCols = visibleCols.filter((c) => c.key !== "_actions");
    return (filteredByFilters || []).filter((r, i) =>
      searchableCols.some((c) =>
        toStr(typeof c.get === "function" ? c.get(r, i) : r?.[c.key])
          .toLowerCase()
          .includes(term),
      ),
    );
  }, [filteredByFilters, search, visibleCols]);

  // paging
  useEffect(() => {
    setPage(0);
  }, [search, fStatus, fCompany, fFrom, fTo, fMinGrand, fMaxGrand, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pageRows = filteredRows.slice(page * pageSize, (page + 1) * pageSize);

  // chips
  const chips = useMemo(() => {
    const out = [];
    if (String(fStatus || "").toLowerCase() !== "all" && fStatus)
      out.push({ k: "Status", v: fStatus });
    if (fCompany.trim()) out.push({ k: "Company", v: fCompany.trim() });
    if (fFrom) out.push({ k: "From", v: fFrom });
    if (fTo) out.push({ k: "To", v: fTo });
    if (fMinGrand !== "") out.push({ k: "Min Total", v: fMinGrand });
    if (fMaxGrand !== "") out.push({ k: "Max Total", v: fMaxGrand });
    return out;
  }, [fStatus, fCompany, fFrom, fTo, fMinGrand, fMaxGrand]);

  const clearFilters = () => {
    setFStatus("All");
    setFCompany("");
    setFFrom("");
    setFTo("");
    setFMinGrand("");
    setFMaxGrand("");
  };

  // ---------------- EXPORTS (buttons like other pages) ----------------
  const exportCols = useMemo(
    () => visibleCols.filter((c) => c.key !== "_actions"),
    [visibleCols],
  );

  const exportPDF = async () => {
    const csv = buildCSV(filteredRows, exportCols);
    await exportDataToPDF(csv, "PendingPurchaseList");
  };

  const exportCSV = () => {
    const csv = buildCSV(filteredRows, exportCols);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "PendingPurchaseList.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportXLS = async () => {
    const csv = buildCSV(filteredRows, exportCols);
    await convertDataCSVtoExcel(csv, "PendingPurchaseList");
  };

  // loading UI
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows.length;

  return (
    <>
      {/* === Toolbar === */}
      <Row className="po-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card className="po-card">
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div className="po-title">
                Pending Purchase List{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
                <span
                  style={{ fontWeight: 800, color: "#64748b", fontSize: 12 }}
                >
                  (Total: {rows.length})
                </span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              {masterShow ? (
                <div style={{ minWidth: 320, display: "flex", gap: 8 }}>
                  <SuperAdminUI
                    onDropdownChange={onMasterChange}
                    onSubmit={onMasterSubmit}
                  />
                </div>
              ) : null}

              <div className="po-actions">
                <div className="po-search">
                  <Input
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ background: "#fff" }}
                  />
                </div>

                <Button
                  className="po-btn-outline"
                  outline
                  color="primary"
                  onClick={() => setShowFilters((s) => !s)}
                  title="Show/Hide Filters"
                >
                  <FaFilter size={14} /> Filters
                </Button>

                {permissions?.View && (
                  <Button
                    className="po-btn-outline"
                    outline
                    color="primary"
                    onClick={openPicker}
                    title="Choose Columns"
                  >
                    <FaFilter size={14} /> Columns
                  </Button>
                )}

                {permissions?.Download && (
                  <>
                    <Button
                      className="po-btn"
                      color="primary"
                      onClick={exportXLS}
                      title="Download Excel"
                    >
                      <ImDownload /> Excel
                    </Button>
                    <Button
                      className="po-btn-outline"
                      outline
                      color="primary"
                      onClick={exportPDF}
                      title="Download PDF"
                    >
                      <ImDownload /> PDF
                    </Button>
                    <Button
                      className="po-btn-outline"
                      outline
                      color="primary"
                      onClick={exportCSV}
                      title="Download CSV"
                    >
                      <ImDownload /> CSV
                    </Button>
                  </>
                )}
              </div>
            </div>

            {errorMsg ? (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  background: "#fff",
                  borderRadius: 12,
                  border: "1px solid #fecaca",
                  color: "#b91c1c",
                  fontWeight: 800,
                }}
              >
                {errorMsg}
              </div>
            ) : null}

            {/* ===== Filters Panel ===== */}
            {showFilters && (
              <div className="po-filterbar">
                <div className="po-filter-row">
                  <div className="po-fitem">
                    <label>Status</label>
                    <Input
                      type="select"
                      value={fStatus}
                      onChange={(e) => setFStatus(e.target.value)}
                    >
                      <option value="All">All</option>
                      <option value="Received">Received</option>
                      <option value="Pending">Pending</option>
                      <option value="Completed">Completed</option>
                      <option value="Deactive">Deactive</option>
                    </Input>
                  </div>

                  <div className="po-fitem">
                    <label>Company (contains)</label>
                    <Input
                      placeholder="Type company name..."
                      value={fCompany}
                      onChange={(e) => setFCompany(e.target.value)}
                    />
                  </div>

                  <div className="po-fitem">
                    <label>Date From</label>
                    <Input
                      type="date"
                      value={fFrom}
                      onChange={(e) => setFFrom(e.target.value)}
                    />
                  </div>

                  <div className="po-fitem">
                    <label>Date To</label>
                    <Input
                      type="date"
                      value={fTo}
                      onChange={(e) => setFTo(e.target.value)}
                    />
                  </div>

                  <div className="po-fitem">
                    <label>Min Grand Total</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={fMinGrand}
                      onChange={(e) => setFMinGrand(e.target.value)}
                    />
                  </div>

                  <div className="po-fitem">
                    <label>Max Grand Total</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={fMaxGrand}
                      onChange={(e) => setFMaxGrand(e.target.value)}
                    />
                  </div>

                  <div className="po-fitem" style={{ minWidth: 220 }}>
                    <label>Actions</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button
                        className="po-btn-outline"
                        outline
                        color="secondary"
                        onClick={clearFilters}
                      >
                        Clear
                      </Button>
                      <Button
                        className="po-btn-outline"
                        outline
                        color="primary"
                        onClick={() => {
                          const u = JSON.parse(
                            localStorage.getItem("userData") || "{}",
                          );
                          const financialYearSelected = localStorage.getItem(
                            "financialYearSelected",
                          );
                          const selectedDatabase = `${u?.database}-${financialYearSelected}`;
                          reload(u?._id, selectedDatabase);
                        }}
                        title="Refresh"
                      >
                        Refresh
                      </Button>
                    </div>
                  </div>
                </div>

                {!!chips.length && (
                  <div className="po-chipline">
                    {chips.map((c, i) => (
                      <span className="po-chip" key={`${c.k}-${i}`}>
                        {c.k}: {c.v}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* === Table Card === */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No data found.
              </div>
            ) : (
              <>
                <div className="po-table-wrap">
                  <table className="po-table">
                    <thead>
                      <tr>
                        {visibleCols.map((c) => (
                          <th key={c.key}>{c.header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((r, ri) => (
                        <tr key={r?._id || `${ri}-${r?.invoiceId || ""}`}>
                          {visibleCols.map((c, ci) => {
                            if (
                              typeof c.render === "function" &&
                              c.key === "_actions"
                            ) {
                              return <td key={`${ri}-${ci}`}>{c.render(r)}</td>;
                            }
                            const value =
                              typeof c.get === "function"
                                ? c.get(r, page * pageSize + ri)
                                : r?.[c.key];
                            return <td key={`${ri}-${ci}`}>{toStr(value)}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* paging */}
                <div className="paging">
                  <div className="nowrap" style={{ marginRight: 8 }}>
                    Rows per page:
                  </div>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(0);
                    }}
                    style={{ height: 28 }}
                  >
                    {[10, 15, 20, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>

                  <div className="nowrap" style={{ marginLeft: 8 }}>
                    {filteredRows.length
                      ? `${page * pageSize + 1}-${Math.min(
                          (page + 1) * pageSize,
                          filteredRows.length,
                        )} of ${filteredRows.length}`
                      : "0-0 of 0"}
                  </div>

                  <Button
                    size="sm"
                    disabled={page <= 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    Prev
                  </Button>
                  <Button
                    size="sm"
                    disabled={page >= totalPages - 1}
                    onClick={() =>
                      setPage((p) => Math.min(totalPages - 1, p + 1))
                    }
                  >
                    Next
                  </Button>
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* === Column Chooser Modal === */}
      <Modal isOpen={modal} toggle={closePicker} style={{ maxWidth: 1050 }}>
        <ModalHeader toggle={closePicker}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div className="picker-list">
                {baseColumns
                  .filter((c) => c.key !== "_actions")
                  .map((col) => (
                    <label
                      key={col.key}
                      className="pick-item"
                      style={{ cursor: "default" }}
                    >
                      <input
                        type="checkbox"
                        checked={checkedToAdd.includes(col.key)}
                        onChange={(e) =>
                          handleCheckToAdd(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col
              lg="2"
              md="2"
              sm="12"
              xl="2"
              xs="12"
              style={{ display: "grid", placeItems: "center" }}
            >
              <div style={{ display: "grid", gap: 10 }}>
                <FaArrowAltCircleRight
                  onClick={addSelectedColumns}
                  size={30}
                  style={{ cursor: "pointer" }}
                  title="Add"
                />
                <FaArrowAltCircleLeft
                  onClick={removeAtIndex}
                  size={30}
                  style={{ cursor: "pointer" }}
                  title="Remove selected"
                />
              </div>
            </Col>

            <Col lg="6" md="6" sm="12" xl="6" xs="12">
              <Row>
                <Col lg="8" md="8" sm="12" xs="12">
                  <h5>Visible Columns</h5>
                  <div className="picker-list">
                    {selectedKeys.map((key, i) => {
                      const col = colMap.get(key);
                      if (!col) return null;
                      return (
                        <div
                          key={key}
                          className="pick-item"
                          onClick={() => setArrIndex(i)}
                          style={{
                            background: i === arrIndex ? "#1877f2" : "#fff",
                            color: i === arrIndex ? "#fff" : "#000",
                          }}
                        >
                          <IoMdRemoveCircleOutline
                            size={22}
                            color={i === arrIndex ? "#fff" : "red"}
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedKeys((prev) => {
                                const next = prev.slice();
                                next.splice(i, 1);
                                return next;
                              });
                              if (arrIndex === i) setArrIndex(-1);
                            }}
                          />
                          <span>{col.header}</span>
                        </div>
                      );
                    })}
                  </div>
                </Col>
                <Col
                  lg="4"
                  md="4"
                  sm="12"
                  xs="12"
                  style={{ display: "grid", placeItems: "center" }}
                >
                  <div style={{ display: "grid", gap: 10 }}>
                    <BsFillArrowUpSquareFill
                      size={30}
                      onClick={shiftUp}
                      style={{ cursor: "pointer" }}
                      title="Move up"
                    />
                    <BsFillArrowDownSquareFill
                      size={30}
                      onClick={shiftDown}
                      style={{ cursor: "pointer" }}
                      title="Move down"
                    />
                  </div>
                </Col>
              </Row>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* === View Modal === */}
      <Modal
        isOpen={viewOpen}
        toggle={() => setViewOpen(false)}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={() => setViewOpen(false)}>
          Purchase View
        </ModalHeader>
        <ModalBody>
          {!viewData ? null : (
            <>
              <Row>
                <Col>
                  <Label>Company Name:</Label>
                  <h5>{viewData?.partyId?.CompanyName || "-"}</h5>
                </Col>
                <Col>
                  <Label>Amount:</Label>
                  <h5>{toStr(viewData?.amount)}</h5>
                </Col>

                {Number(viewData?.igstTaxType) === 1 ? (
                  <Col>
                    <Label>IGST:</Label>
                    <h5>
                      <strong>{toStr(viewData?.igstTotal)}</strong> Rs/-
                    </h5>
                  </Col>
                ) : (
                  <>
                    <Col>
                      <Label>SGST:</Label>
                      <h5>
                        <strong>{toStr(viewData?.sgstTotal)}</strong> Rs/-
                      </h5>
                    </Col>
                    <Col>
                      <Label>CGST:</Label>
                      <h5>
                        <strong>{toStr(viewData?.cgstTotal)}</strong> Rs/-
                      </h5>
                    </Col>
                  </>
                )}

                <Col>
                  <Label>Grand Total:</Label>
                  <h5>
                    <strong>{toStr(viewData?.grandTotal)}</strong> Rs/-
                  </h5>
                </Col>

                <Col>
                  <Label>Status:</Label>
                  <h5>
                    <Badge
                      color={
                        includesCompleted(viewData?.status)
                          ? "primary"
                          : "warning"
                      }
                    >
                      {viewData?.status}
                    </Badge>
                  </h5>
                </Col>
              </Row>

              <Row className="p-2">
                <Col>
                  <div className="d-flex justify-content-center">
                    <h4>Sales Order List</h4>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Table responsive style={{ cursor: "pointer" }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product Name</th>
                        <th>HSN CODE</th>
                        <th>Price</th>
                        <th>Quantity</th>
                        <th>Unit</th>
                        <th>Taxable</th>
                        {Number(viewData?.igstTaxType) === 1 ? (
                          <th>IGST</th>
                        ) : (
                          <>
                            <th>SGST</th>
                            <th>CGST</th>
                          </>
                        )}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(viewData?.orderItems) &&
                        viewData.orderItems.map((it, i) => (
                          <tr key={`${i}-${it?.productId?._id || ""}`}>
                            <th scope="row">{i + 1}</th>
                            <td>{it?.productId?.Product_Title || "-"}</td>
                            <td>{it?.productId?.HSN_Code || "-"}</td>
                            <td>{toStr(it?.basicPrice)}</td>
                            <td>{toStr(it?.qty)}</td>
                            <td>{toStr(it?.productId?.primaryUnit)}</td>
                            <td>{toStr(it?.taxableAmount)}</td>
                            {Number(viewData?.igstTaxType) === 1 ? (
                              <td>{toStr(it?.igstRate)}</td>
                            ) : (
                              <>
                                <td>{toStr(it?.sgstRate)}</td>
                                <td>{toStr(it?.cgstRate)}</td>
                              </>
                            )}
                            <td>{toStr(it?.grandTotal)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}
